import { isAxiosError, type AxiosInstance } from "axios";
import { apiClient, internalApiClient } from "@/marketing/infrastructure/http/httpClient";
import type {
  AddCampaignInfluencersRequest,
  AddCampaignInfluencersResponse,
  BulkImportCampaignInfluencersRequest,
  BulkImportCampaignInfluencersResponse,
  CampaignInfluencer,
  DeleteContentRequest,
  SetCampaignInfluencersBatchRequest,
  SetCampaignInfluencersBatchResponse,
  SetCampaignInfluencerTagsRequest,
  SetContentSparkAdsCodeRequest,
  SetContentUrlsRequest,
  SetRejectedRequest,
  SetSelectedSkusRequest,
  SetShippingRequest,
  TransitionStageRequest,
} from "@/marketing/models/campaignInfluencer";
import { pathSegment } from "@/marketing/lib/pathSegment";
import {
  STANDARD_PIPELINE_DEFINITION,
  type CampaignPipelineDefinition,
} from "@/marketing/models/campaignPipeline";

const BASE_PATH = "/influencer/campaigns";
const PIPELINES_PATH = "/influencer/campaignPipelines";

export interface ListCampaignInfluencersParams {
  stage?: string;
}

export class CampaignPipelineAPIClient {
  // Pipeline definitions, tags, batches, and content Add/Delete use internal-api; legacy replacements and other routes use the external facade.
  constructor(
    private readonly client: AxiosInstance = apiClient,
    private readonly internalClient: AxiosInstance = internalApiClient,
  ) {}

  // A 404 means the facade predates pipeline definitions, so only the bundled Standard pipeline exists.
  public async listCampaignPipelines(): Promise<CampaignPipelineDefinition[]> {
    try {
      const { data } =
        await this.internalClient.get<CampaignPipelineDefinition[]>(PIPELINES_PATH);
      return data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return [STANDARD_PIPELINE_DEFINITION];
      }
      throw error;
    }
  }

  public async listCampaignInfluencers(
    campaignId: string,
    params: ListCampaignInfluencersParams = {},
    signal?: AbortSignal,
  ): Promise<CampaignInfluencer[]> {
    const { data } = await this.client.get<CampaignInfluencer[]>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers`,
      { params, ...(signal ? { signal } : {}) },
    );
    return data;
  }

  public async getCampaignInfluencer(
    campaignId: string,
    influencerId: string,
  ): Promise<CampaignInfluencer> {
    const { data } = await this.client.get<CampaignInfluencer>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}`,
    );
    return data;
  }

  public async addCampaignInfluencers(
    campaignId: string,
    request: AddCampaignInfluencersRequest,
  ): Promise<AddCampaignInfluencersResponse> {
    const { data } = await this.client.post<AddCampaignInfluencersResponse>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers`,
      request,
    );
    return data;
  }

  public async bulkImportCampaignInfluencers(
    campaignId: string,
    request: BulkImportCampaignInfluencersRequest,
  ): Promise<BulkImportCampaignInfluencersResponse> {
    const { data } = await this.client.post<BulkImportCampaignInfluencersResponse>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/bulkImport`,
      request,
    );
    return data;
  }

  public async transitionCampaignInfluencerStage(
    campaignId: string,
    influencerId: string,
    request: TransitionStageRequest,
  ): Promise<CampaignInfluencer> {
    const { data } = await this.client.patch<CampaignInfluencer>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/stage`,
      request,
    );
    return data;
  }

  public async setCampaignInfluencerSelectedSkus(
    campaignId: string,
    influencerId: string,
    request: SetSelectedSkusRequest,
  ): Promise<CampaignInfluencer> {
    const { data } = await this.client.patch<CampaignInfluencer>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/selectedSkus`,
      request,
    );
    return data;
  }

  public async setCampaignInfluencerShipping(
    campaignId: string,
    influencerId: string,
    request: SetShippingRequest,
  ): Promise<CampaignInfluencer> {
    const { data } = await this.client.patch<CampaignInfluencer>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/shipping`,
      request,
    );
    return data;
  }

  public async setCampaignInfluencerContentUrls(
    campaignId: string,
    influencerId: string,
    request: SetContentUrlsRequest,
  ): Promise<CampaignInfluencer> {
    const client = request.submission !== undefined ? this.internalClient : this.client;
    const { data } = await client.patch<CampaignInfluencer>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/contentUrls`,
      request,
    );
    return data;
  }

  public async deleteCampaignInfluencerContent(
    campaignId: string,
    influencerId: string,
    request: DeleteContentRequest,
  ): Promise<CampaignInfluencer> {
    const { data } = await this.internalClient.delete<CampaignInfluencer>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/contentSubmissions`,
      { params: request },
    );
    return data;
  }

  public async setCampaignInfluencerContentSparkAdsCode(
    campaignId: string,
    influencerId: string,
    request: SetContentSparkAdsCodeRequest,
  ): Promise<CampaignInfluencer> {
    const { data } = await this.client.patch<CampaignInfluencer>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/contentSubmissions/sparkAdsCode`,
      request,
    );
    return data;
  }

  public async setCampaignInfluencerRejected(
    campaignId: string,
    influencerId: string,
    request: SetRejectedRequest,
  ): Promise<CampaignInfluencer> {
    const { data } = await this.client.patch<CampaignInfluencer>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/rejected`,
      request,
    );
    return data;
  }

  public async setCampaignInfluencerTags(
    campaignId: string,
    influencerId: string,
    request: SetCampaignInfluencerTagsRequest,
  ): Promise<CampaignInfluencer> {
    const { data } = await this.internalClient.patch<CampaignInfluencer>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/tags`,
      request,
    );

    return data;
  }

  public async setCampaignInfluencersBatch(
    campaignId: string,
    request: SetCampaignInfluencersBatchRequest,
  ): Promise<SetCampaignInfluencersBatchResponse> {
    // Admin-only batch assignment lives on internal-api, like tag setting.
    const { data } = await this.internalClient.patch<SetCampaignInfluencersBatchResponse>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/batch`,
      request,
    );
    return data;
  }
}

export const campaignPipelineAPIClient = new CampaignPipelineAPIClient();
